import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks.ts";
import { useParams } from "react-router-dom";
import { useGenerateJWT } from "../../hooks/vault/mutations";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  clearStoredVaultActorSession,
  hasStoredVaultActorSessionForFolder,
} from "../../utils/vault/vaultActorSession.ts";
import { clearJWT } from "../../redux/slices/vaultSlice.ts";

interface Props {
  children: React.ReactNode;
}

export const IsActorIdentifiedMiddleware: React.FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const generateJWT = useGenerateJWT();
  const params = useParams();

  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { jwtToken, loading: reduxLoading } = useAppSelector(
    (state) => state.vault,
  );
  const hasValidActorSession = Boolean(
    jwtToken && hasStoredVaultActorSessionForFolder(params.id),
  );

  const validateEmail = (value: string): string | null => {
    if (!value.trim()) return "Campo requerido.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Correo electrónico inválido.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    await handleTenantRoutes(email.toLowerCase());
  };

  const handleTenantRoutes = async (emailValue?: string) => {
    const folder_id = params.id;

    if (emailValue && !hasValidActorSession) {
      await handleGenerateJWT(folder_id!, emailValue);
    }
  };

  const handleGenerateJWT = async (folder_id: string, emailValue: string) => {
    try {
      await generateJWT.mutateAsync({ folderId: folder_id, email: emailValue });
      setIsOpened(false);
    } catch (error: any) {
      // The API interceptor rewrites 401/403 as
      // `{ response: { data: { code: "I000" } } }` — a plain object, NOT an
      // Error. Rendering it via `toast.error(error)` crashes React with
      // "Objects are not valid as a React child".
      const code = error?.response?.data?.code;
      if (code === "I000") {
        setError(
          "Este correo no tiene acceso al expediente. Verifica que sea el registrado en la solicitud.",
        );
        return;
      }

      const rawMessage =
        error?.response?.data?.message ??
        (typeof error?.message === "string" ? error.message : null);
      toast.error(
        typeof rawMessage === "string" && rawMessage.length > 0
          ? rawMessage
          : "No se pudo acceder al expediente. Intenta de nuevo más tarde.",
      );
    }
  };

  useEffect(() => {
    setIsOpened(true);
  }, [params.id]);

  useEffect(() => {
    if (!jwtToken || hasValidActorSession) return;

    clearStoredVaultActorSession();
    dispatch(clearJWT());
  }, [dispatch, hasValidActorSession, jwtToken]);

  useEffect(() => {
    if (!params.id) return;

    handleTenantRoutes();
  }, [params.id, hasValidActorSession, generateJWT]);

  if (hasValidActorSession) return <div>{children}</div>;

  return (
    <div className="w-full h-screen flex justify-center items-center">
      <Dialog open={isOpened} onOpenChange={setIsOpened}>
        <DialogContent
          showCloseButton={false}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Acceso al expediente</DialogTitle>
            <DialogDescription>
              Captura la información solicitada para acceder a la solicitud de
              renta.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="space-y-2">
              <Label htmlFor="email">
                Correo registrado en la solicitud{" "}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="ej: juan@gmail.com"
                className="lowercase"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                aria-invalid={!!error}
              />
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
          </form>

          <DialogFooter>
            <Button
              type="submit"
              variant="dark"
              disabled={generateJWT.isPending || reduxLoading}
              onClick={handleSubmit}
            >
              {(generateJWT.isPending || reduxLoading) && (
                <Loader2 className="size-4 animate-spin" />
              )}
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
